import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'apps/auth/components/common/Button/Button';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { usePageMessages } from 'apps/auth/hooks/selectors';
import { MarkdownService } from 'apps/auth/services/MarkdownService';

import { buildQrFilename, exportReferralCardToPng } from './exportReferralCard';
import ReferralQRCode from './ReferralQRCode';
import styles from './referralQRCode.module.css';
import ReferralQRCodeCard from './ReferralQRCodeCard';

const QR_FILE_TYPE = 'image/png';

const isShareCancelled = (error) => error?.name === 'AbortError';

const downloadBlob = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

const createQrFile = (blob, filename) => {
    return new File([blob], filename, {
        type: blob.type || QR_FILE_TYPE,
        lastModified: Date.now(),
    });
};

const shareBlobSync = (blob, referralLink) => {
    const filename = buildQrFilename(referralLink);
    const file = createQrFile(blob, filename);
    navigator.share({ files: [file] }).catch((error) => {
        if (isShareCancelled(error)) return;
        console.error('QR share failed', error);
    });
};

const hasNativeFileShare = () => {
    if (typeof navigator === 'undefined') return false;
    if (typeof navigator.share !== 'function') return false;
    if (typeof navigator.canShare !== 'function') return false;
    if (typeof File !== 'function') return false;
    try {
        const probe = new File([new Uint8Array([0])], 'probe.png', { type: QR_FILE_TYPE });
        return navigator.canShare({ files: [probe] });
    } catch {
        return false;
    }
};

const ShareOrDownloadQRCodeModal = ({ modalRef, referralLink, userName }) => {
    const { shareQrCodeTitlePurple, shareQrCodeTitle, shareQrCodeButton, downloadQrCodeButton, qrCodeCardTitle, qrCodeCardText1, qrCodeCardText2 } =
        usePageMessages();

    const exportNodeRef = useRef(null);
    const blobRef = useRef(null);
    const exportPromiseRef = useRef(null);
    const pendingShareRef = useRef(false);
    const [isPreparing, setIsPreparing] = useState(false);
    const [canNativeShare] = useState(hasNativeFileShare);

    useEffect(() => {
        if (!referralLink || !exportNodeRef.current) return undefined;
        let cancelled = false;
        const promise = exportReferralCardToPng(exportNodeRef.current);
        exportPromiseRef.current = promise;
        promise
            .then((blob) => {
                if (cancelled) return;
                blobRef.current = blob;
                if (!pendingShareRef.current) return;
                pendingShareRef.current = false;
                setIsPreparing(false);
                shareBlobSync(blob, referralLink);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('QR export failed', error);
                if (!pendingShareRef.current) return;
                pendingShareRef.current = false;
                setIsPreparing(false);
            });
        return () => {
            cancelled = true;
        };
    }, [referralLink]);

    const handleShare = (event) => {
        event?.preventDefault();
        if (!referralLink || isPreparing) return;
        const blob = blobRef.current;
        if (blob) {
            shareBlobSync(blob, referralLink);
            return;
        }
        pendingShareRef.current = true;
        setIsPreparing(true);
    };

    const handleDownload = async (event) => {
        event?.preventDefault();
        if (!referralLink) return;
        const filename = buildQrFilename(referralLink);
        try {
            const blob = blobRef.current || (exportPromiseRef.current && (await exportPromiseRef.current));
            if (!blob) return;
            downloadBlob(blob, filename);
        } catch (error) {
            console.error('QR download failed', error);
        }
    };

    return (
        <ModalComponent modalRef={modalRef} isIconClose>
            <Row indent={'64px'}>
                <Text variant="div" size="header" color="primary">
                    {MarkdownService.removeNewlines(shareQrCodeTitlePurple)}
                </Text>
                <Text variant="div" size="header">
                    {shareQrCodeTitle}
                </Text>
            </Row>
            <div className={styles.qrSlot}>
                <div className={styles.qrFrame}>
                    <ReferralQRCode sharedLink={referralLink} />
                </div>
            </div>
            {/*hide the button if the functionality is not available (ex. Firefox)*/}
            {canNativeShare && (
                <Row indent="16px" flex justify="center">
                    <Button
                        type="button"
                        caption={isPreparing ? 'Preparing…' : shareQrCodeButton}
                        title={shareQrCodeButton}
                        onClick={handleShare}
                        disabled={isPreparing}
                    />
                </Row>
            )}
            <Row flex justify="center">
                <Button type="button" variant={'secondary'} caption={downloadQrCodeButton} title="Download QR code" onClick={handleDownload} />
            </Row>
            <div ref={exportNodeRef} className={styles.exportContainer} aria-hidden="true">
                <ReferralQRCodeCard name={userName} referralLink={referralLink} title={qrCodeCardTitle} text1={qrCodeCardText1} text2={qrCodeCardText2} />
            </div>
        </ModalComponent>
    );
};

export default ShareOrDownloadQRCodeModal;
